import type { GameState, GamePhase } from '@/game/types'
import { displayedOpponentRecord } from '../game/season'
import { CLUB_PROJECT_CATALOGUE } from '../game/clubStrategy'
import { hasPendingLeftFieldEvent } from '../game/leftField'
import { teamSelectionIssue } from '../game/injuries'
import { isTeamAliveInFinals } from '../game/competition'

interface DashboardProps {
  state: GameState
  onAction: (phase: GamePhase, playerId?: string) => void
  onAdvance: () => void
}

type ActionStatus = 'required' | 'optional' | 'handled' | 'unavailable'

interface WeeklyAction {
  phase: GamePhase
  label: string
  description: string
  status: ActionStatus
  statusLabel: string
  badge?: string
}

const facilityLabels = {
  'training-ground': 'Training',
  'medical-centre': 'Medical',
  stadium: 'Stadium',
  academy: 'Academy',
} as const

const specialtyLabels = {
  attack: 'Attack',
  defence: 'Defence',
  development: 'Development',
  conditioning: 'Conditioning',
} as const

const formatLedgerMoney = (amount: number, showSign = false) => {
  const sign = amount < 0 ? '−' : showSign && amount > 0 ? '+' : ''
  return `${sign}$${Math.abs(amount).toLocaleString()}k`
}

function recentCashMovement(state: GameState) {
  const entries = state.cashFlowLedger.entries.filter(entry => entry.id !== 'cf-initial')
  const latest = entries.at(-1)
  if (!latest) return { amount: 0, label: 'No operating entries yet' }
  const amount = entries
    .filter(entry => entry.season === latest.season && entry.round === latest.round)
    .reduce((total, entry) => total + entry.amount, 0)
  return { amount, label: latest.round > 0 ? `Season ${latest.season}, Round ${latest.round}` : `Season ${latest.season} close` }
}

function getWeeklyActions(state: GameState): WeeklyAction[] {
  const selectionIssue = teamSelectionIssue(state.staffPlan.team, state.club.players)
  const developmentAllocated = state.staffPlan.development.reduce((total, allocation) => total + allocation.points, 0)
  const developmentComplete = developmentAllocated === state.club.developmentCapacity
  const tacticalOverrides = state.tactics.filter(tactic => tactic.currentChoice !== tactic.staffChoice).length
    + (state.conditionalInstruction?.id === 'D7-A' ? 0 : 1)
  const researchCount = state.researchPlan.topics.filter(topic => topic.isSelected).length
  const researchSpent = state.staffPlan.research.points
  const selectionModified = state.decisions.D2.currentChoice !== 'A'
  const developmentModified = state.staffPlan.development.some(allocation => allocation.isLocked)

  const { objective, project, stakeholders, annualPlan } = state.clubStrategy
  const objectiveProgress = objective.progress
  const isLadder = objective.type === 'ladder-position'
  const objectiveCurrent = isLadder ? (objectiveProgress.current > 0 ? objectiveProgress.current : '—') : objectiveProgress.current
  const objectiveTarget = objective.target.value
  const projectTitle = project.selectedId ? CLUB_PROJECT_CATALOGUE.find(p => p.id === project.selectedId)?.title : 'None selected'
  const projectProgress = project.progress.target > 0 ? Math.round((project.progress.current / project.progress.target) * 100) : 0
  const boardConfidence = stakeholders.board.confidence

  // In Round 1, if annual coach plan isn't locked, promote Staff to first required action
  const isRound1 = state.round === 1 && state.preseasonComplete === true
  const staffPlanUnlocked = isRound1 && !state.coachDevelopment.isLocked

  const staffAction: WeeklyAction = {
    phase: 'staff',
    label: 'Staff',
    description: state.coachDevelopment.isLocked
      ? 'The Season 1 coach-development plan is locked. No weekly action is needed.'
      : 'Staff have prepared a complete season development plan. Review it now or retain their allocations.',
    status: state.coachDevelopment.isLocked
      ? 'handled'
      : state.preseasonComplete === true
        ? 'required'
        : 'optional',
    statusLabel: state.coachDevelopment.isLocked
      ? 'Staff handled — season plan locked'
      : state.preseasonComplete === true
        ? 'Action required — annual coach plan not locked'
        : 'Review optional — staff plan set',
    badge: `${state.coachDevelopment.totalPoints} pts`,
  }

  const baseActions: WeeklyAction[] = [
    {
      phase: 'team',
      label: 'Team Selection',
      description: selectionIssue ?? (selectionModified ? 'The Coach has changed the selected side. This modified team will be used at match day.' : 'Staff have selected the 22 and assigned every position. Review only if you want to intervene.'),
      status: selectionIssue ? 'required' : 'optional',
      statusLabel: selectionIssue ? 'Action required — unavailable team' : selectionModified ? 'Review optional — Coach-modified team' : 'Review optional — staff team set',
      badge: '18+4',
    },
    {
      phase: 'development',
      label: 'Development',
      description: developmentComplete
        ? developmentModified ? 'The Coach has modified the development allocation. All capacity is assigned.' : 'Staff have allocated all development capacity. Their plan will apply unless you change it.'
        : `${state.club.developmentCapacity - developmentAllocated} development point${state.club.developmentCapacity - developmentAllocated === 1 ? '' : 's'} must be allocated before match day.`,
      status: developmentComplete ? 'optional' : 'required',
      statusLabel: developmentComplete ? developmentModified ? 'Review optional — Coach-modified plan' : 'Review optional — staff plan set' : 'Action required — allocation incomplete',
      badge: `${developmentAllocated}/${state.club.developmentCapacity} pts`,
    },
    {
      phase: 'tactics-review',
      label: 'Tactics',
      description: tacticalOverrides > 0
        ? `${tacticalOverrides} Coach override${tacticalOverrides === 1 ? '' : 's'} will replace the staff recommendation at match day.`
        : 'Staff have set ball movement, width, contests, defence, tempo and a conditional instruction.',
      status: 'optional',
      statusLabel: tacticalOverrides > 0 ? `Review optional — ${tacticalOverrides} Coach override${tacticalOverrides === 1 ? '' : 's'}` : 'Review optional — staff tactics set',
    },
    {
      phase: 'research',
      label: 'Research',
      description: researchCount
        ? `${researchCount} investigation${researchCount === 1 ? '' : 's'} selected; ${researchSpent} capacity committed and ${state.club.resources.research} retained.`
        : 'The free staff report is complete. Spend capacity only if you want higher-confidence tactical intelligence.',
      status: 'optional',
      statusLabel: researchCount ? `Review optional — ${researchCount} investigation${researchCount === 1 ? '' : 's'} active` : 'Review optional — staff report ready',
    },
    {
      phase: 'club',
      label: 'Club',
      description: annualPlan.locked
        ? `Season ${annualPlan.season} plan locked. Project: ${projectTitle} (${projectProgress}%). Board: ${boardConfidence}. Objective: ${objectiveCurrent}/${objectiveTarget}.`
        : `Season ${annualPlan.season} planning. Project: ${projectTitle}. Board: ${boardConfidence}. Objective: ${objectiveCurrent}/${objectiveTarget}.`,
      status: 'optional',
      statusLabel: annualPlan.locked ? 'Season plan locked — review status' : 'Review optional — annual plan active',
      badge: projectTitle !== 'None selected' ? projectTitle : undefined,
    },
    {
      phase: 'recruitment',
      label: 'Recruitment',
      description: state.recruitment.watchlist.length
        ? `${state.recruitment.watchlist.length} prospects watched. Review scouting confidence or update private end-of-season trade intentions.`
        : 'The national pool contains 76 prospects. Build a watchlist and set end-of-season trade intent.',
      status: 'optional',
      statusLabel: state.recruitment.watchlist.length ? 'Review optional — recruitment activity saved' : 'Review optional — staff pool ready',
      badge: `${state.recruitment.watchlist.length} watched`,
    },
    {
      phase: 'confirmation',
      label: 'Match Day',
      description: 'The staff plan is valid and ready for final confirmation and seeded match resolution.',
      status: 'handled',
      statusLabel: 'Staff handled — ready to proceed',
    },
  ]

  // In Round 1 with unlocked coach plan, put Staff first as required action
  if (staffPlanUnlocked) {
    return [staffAction, ...baseActions]
  }

  // Otherwise insert staff in its normal position (after club, before recruitment)
  return [
    ...baseActions.slice(0, 4), // team, development, tactics, research
    staffAction,
    ...baseActions.slice(4) // club, recruitment, confirmation
  ]
}

function getWeeklyActionsWithLeftField(state: GameState): WeeklyAction[] {
  const actions = getWeeklyActions(state)
  if (hasPendingLeftFieldEvent(state)) {
    const event = state.leftField.currentEvent!
    actions.unshift({
      phase: 'left-field',
      label: 'Out of Left Field',
      description: `${event.title} — ${event.severity ?? 'moderate'} interruption. Resolve it before match day.`,
      status: 'required',
      statusLabel: `Action required — ${event.severity ?? 'moderate'} interruption`,
      badge: event.title,
    })
  }
  return actions
}

export function Dashboard({ state, onAction, onAdvance }: DashboardProps) {
  const isBye = state.week?.venue === 'bye'
  const isFinals = state.round > 19 && state.season?.finals?.status === 'active'
  const kingsportFinalsAlive = Boolean(isFinals && state.season?.finals && isTeamAliveInFinals(state.season.finals, state.season.userTeamId))
  const opponentRecord = displayedOpponentRecord(state)
  const debut = state.decisions.D2?.currentChoice === 'B'

  const { objective, project, stakeholders, annualPlan } = state.clubStrategy
  const objectiveProgress = objective.progress
  const isLadder = objective.type === 'ladder-position'
  const objectiveCurrent = isLadder ? (objectiveProgress.current > 0 ? objectiveProgress.current : '—') : objectiveProgress.current
  const objectiveTarget = objective.target.value
  const projectTitle = project.selectedId ? CLUB_PROJECT_CATALOGUE.find(p => p.id === project.selectedId)?.title : 'None selected'
  const projectProgress = project.progress.target > 0 ? Math.round((project.progress.current / project.progress.target) * 100) : 0
  const boardConfidence = stakeholders.board.confidence
  const capUsed = state.salaryCap.committedCap + state.salaryCap.deadCap
  const expiringContracts = Object.values(state.contracts).filter(contract => contract.yearsRemaining === 1 && contract.status !== 'delisted').length
  const activeBuild = state.facilities.activeBuild
  const highestPoachingRisk = state.staffMarket.roster.reduce((highest, coach) => Math.max(highest, coach.poachingRisk), 0)
  const sponsorsByTier = (['major', 'secondary', 'community'] as const).map(tier => ({
    tier,
    sponsors: state.sponsorship.sponsors.filter(sponsor => sponsor.tier === tier),
  }))
  const cashMovement = recentCashMovement(state)

  const actions = getWeeklyActionsWithLeftField(state).filter(action => !isBye || action.phase !== 'confirmation')
  const requiredActions = actions.filter(action => action.status === 'required')
  const optionalCount = actions.filter(action => action.status === 'optional').length
  const handledCount = actions.filter(action => action.status === 'handled').length
  const isReady = requiredActions.length === 0
  const coachModifiedAreas = [
    state.decisions.D2.currentChoice !== 'A' ? 'team' : null,
    state.staffPlan.development.some(allocation => allocation.isLocked) ? 'development' : null,
    state.tactics.some(tactic => tactic.currentChoice !== tactic.staffChoice) || state.conditionalInstruction?.id !== 'D7-A' ? 'tactics' : null,
    state.researchPlan.topics.some(topic => topic.isSelected) ? 'research' : null,
  ].filter((area): area is string => Boolean(area))
  const hasCoachChanges = coachModifiedAreas.length > 0
  const handlePrimaryAction = () => {
    if (requiredActions[0]) onAction(requiredActions[0].phase)
    else onAdvance()
  }
  const handleActionCard = (action: WeeklyAction) => {
    if (action.phase !== 'confirmation') {
      onAction(action.phase)
      return
    }
    const matchControl = document.getElementById('match-start-control')
    matchControl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    matchControl?.focus()
  }

  return (
    <div className="screen dashboard">
      <div className="page-title">
        <div>
          <span className="eyebrow">Football department - Week headquarters</span>
          <h1>Round {state.round} Dashboard</h1>
          <p>{state.opponent.assessment}</p>
        </div>
      </div>

      <section className={`weekly-decision-summary ${isReady ? 'ready' : 'blocked'}`} aria-live="polite">
        <div className="decision-summary-copy">
          <span className="decision-summary-icon" aria-hidden="true">{isReady ? '✓' : '!'}</span>
          <div>
            <span className="eyebrow">Weekly decision status</span>
            <h2>{isBye ? isFinals ? 'AI finals progression ready' : 'Bye week — club programme ready' : isReady ? hasCoachChanges ? 'Coach-modified plan ready' : 'No action required — safe to proceed' : `${requiredActions.length} action${requiredActions.length === 1 ? '' : 's'} required`}</h2>
            <p>{isBye ? isFinals ? 'Kingsport has no final this week. This action advances the current finals stage and weekly club processes exactly once.' : 'Kingsport does not play. Completing the week advances club processes once and resolves the other nine league matches.' : isReady ? hasCoachChanges ? `Your changes to ${coachModifiedAreas.join(', ')} are saved and will be used when you start the match.` : 'The staff plan is complete. Review any amber items if you wish, or start the match with the staff choices.' : 'Resolve the red item below before the club can proceed to match day.'}</p>
          </div>
        </div>
        <div className="decision-counts" aria-label="Weekly action totals">
          <div className="required"><strong>{requiredActions.length}</strong><span>Required</span></div>
          <div className="optional"><strong>{optionalCount}</strong><span>Optional</span></div>
          <div className="handled"><strong>{handledCount}</strong><span>Handled</span></div>
        </div>
      </section>

      <div className="club-summary">
        <section className="panel summary-panel">
          <h3>Season Objective</h3>
          <div>
            <div><strong>{objective.title}</strong></div>
            <div><span className="eyebrow">{isLadder ? 'Ladder position' : objective.type === 'young-player-appearances' ? 'Youth senior appearances' : 'Institutional capital'}</span> <strong>{objectiveCurrent}/{objectiveTarget}</strong></div>
            <div><strong>Status:</strong> {objective.status === 'achieved' ? 'Achieved ✓' : objective.status === 'missed' ? 'Missed ✗' : objective.status === 'in-progress' ? 'In progress' : 'Not started'}</div>
            <div><strong>Board confidence:</strong> {boardConfidence}/100</div>
          </div>
        </section>
        <section className="panel summary-panel">
          <h3>Club Project</h3>
          <div>
            <div><strong>{projectTitle}</strong></div>
            {project.selectedId && (
              <>
                <div><strong>Progress:</strong> {project.progress.current}/{project.progress.target} ({projectProgress}%)</div>
                <div><strong>Stage:</strong> {project.status === 'complete' ? 'Complete' : project.stage === 'established' ? 'Established' : 'Foundation'}</div>
                <div><strong>Branch:</strong> {project.selectedBranchId ? 'Selected' : project.branchReady ? 'Ready to choose' : 'Not ready'}</div>
              </>
            )}
            <div><strong>Annual plan:</strong> {annualPlan.locked ? `Locked (Season ${annualPlan.season})` : 'Unlocked — changes apply immediately'}</div>
          </div>
        </section>
        <section className="panel summary-panel">
          <h3>Stakeholder Confidence</h3>
          <div>
            {(Object.keys(stakeholders) as Array<'board'|'supporters'|'seniorPlayers'|'youngPlayers'|'sponsors'>).map(group => (
              <div key={group}><strong>{group === 'seniorPlayers' ? 'Senior Players' : group === 'youngPlayers' ? 'Young Players' : group.charAt(0).toUpperCase() + group.slice(1)}:</strong> {stakeholders[group].confidence}/100 ({stakeholders[group].trend})</div>
            ))}
          </div>
        </section>
        <section className="panel summary-panel">
          <h3>Resources</h3>
          <div>
            <div><strong>Cash:</strong> ${state.club.resources.cash}m</div>
            <div><strong>Research:</strong> {state.club.researchCapacity}/{state.club.maxResearchCapacity} (gen {state.club.weeklyResearchGeneration}/wk)</div>
            <div><strong>Development:</strong> {state.club.developmentCapacity} pts</div>
            <div><strong>Tactical cohesion:</strong> {state.club.tacticalCohesion.toFixed(1)}</div>
          </div>
        </section>
        <section className="panel summary-panel">
          <h3>{isBye ? 'Week Status' : 'Opponent'}</h3>
          <div>
            <div><strong>{isBye ? isFinals ? 'No Kingsport final' : 'Kingsport bye' : state.opponent.name}</strong>{!isBye && !isFinals && ` ${opponentRecord.wins}-${opponentRecord.losses}-${opponentRecord.draws}`}</div>
            <div><strong>{isBye ? 'Programme:' : 'Style:'}</strong> {state.opponent.style}</div>
            {!isBye && <div><strong>Key threat:</strong> {state.opponent.strengths[0]}</div>}
            {!isBye && <div><strong>Key weakness:</strong> {state.opponent.weaknesses[0]}</div>}
          </div>
        </section>
        <section className="panel summary-panel">
          <h3>Squad Health</h3>
          <div>
            <div><strong>Available:</strong> {state.club.players.filter(player => player.fitness > 60 && !player.injury).length} of {state.club.players.length}</div>
            <div><strong>Injured / unavailable:</strong> {state.club.players.filter(player => Boolean(player.injury)).length}</div>
            <div><strong>High form (70+):</strong> {state.club.players.filter(player => player.form > 70).length}</div>
            <div><strong>High morale (70+):</strong> {state.club.players.filter(player => player.morale > 70).length}</div>
            <div><strong>Development active:</strong> {state.staffPlan.development.filter(allocation => allocation.points > 0).length} players</div>
          </div>
        </section>
      </div>

      <section className="operating-summary" aria-labelledby="operating-summary-heading">
        <div className="operating-summary-heading">
          <div>
            <span className="eyebrow">Read-only club position</span>
            <h2 id="operating-summary-heading">Club Operations</h2>
          </div>
          <span>Updated from the current season ledger</span>
        </div>
        <div className="operating-summary-grid">
          <article className="panel operating-card">
            <h3>Salary Cap</h3>
            <strong className="operating-card-value">{formatLedgerMoney(capUsed)} used</strong>
            <div className="operating-meter" aria-label={`${formatLedgerMoney(capUsed)} of ${formatLedgerMoney(state.salaryCap.hardCap)} salary cap used`}>
              <span style={{ width: `${Math.min(100, Math.max(0, (capUsed / state.salaryCap.hardCap) * 100))}%` }} />
            </div>
            <dl>
              <div><dt>Cap limit</dt><dd>{formatLedgerMoney(state.salaryCap.hardCap)}</dd></div>
              <div><dt>Cap space</dt><dd className={state.salaryCap.capSpace < 0 ? 'summary-alert' : ''}>{formatLedgerMoney(state.salaryCap.capSpace)}</dd></div>
              <div><dt>Dead cap</dt><dd>{formatLedgerMoney(state.salaryCap.deadCap)}</dd></div>
              <div><dt>Expiring</dt><dd>{expiringContracts} contract{expiringContracts === 1 ? '' : 's'}</dd></div>
            </dl>
          </article>

          <article className="panel operating-card">
            <h3>Facilities</h3>
            <strong className="operating-card-value">Four-site programme</strong>
            <div className="facility-levels" aria-label="Facility levels">
              {(Object.keys(facilityLabels) as Array<keyof typeof facilityLabels>).map(facility => (
                <span key={facility}><b>{facilityLabels[facility]}</b> Level {state.facilities.levels[facility]}</span>
              ))}
            </div>
            <div className={`operating-note ${activeBuild ? 'active' : ''}`}>
              {activeBuild
                ? <><b>Active build:</b> {facilityLabels[activeBuild.facility]} Level {activeBuild.targetLevel} · {activeBuild.weeksRemaining} week{activeBuild.weeksRemaining === 1 ? '' : 's'} remaining</>
                : <><b>Build queue:</b> No active construction</>}
            </div>
          </article>

          <article className="panel operating-card">
            <h3>Football Staff</h3>
            <strong className="operating-card-value">{state.staffMarket.roster.length} assistants</strong>
            <ul className="operating-roster">
              {state.staffMarket.roster.map(coach => (
                <li key={coach.id}><span>{coach.name}</span><b>{specialtyLabels[coach.specialty]}</b></li>
              ))}
            </ul>
            <div className="operating-note">
              <b>Market:</b> {state.staffMarket.candidates.length} candidates · highest poaching risk {highestPoachingRisk}%
            </div>
          </article>

          <article className="panel operating-card">
            <h3>Sponsors &amp; Cash Flow</h3>
            <strong className="operating-card-value">{formatLedgerMoney(state.cashFlowLedger.currentBalance)} balance</strong>
            <div className="sponsor-tiers">
              {sponsorsByTier.map(({ tier, sponsors }) => (
                <div key={tier}>
                  <b>{tier.charAt(0).toUpperCase() + tier.slice(1)}</b>
                  <span>{sponsors.length ? sponsors.map(sponsor => sponsor.name).join(', ') : 'None'}</span>
                </div>
              ))}
            </div>
            <div className="operating-note">
              <b>Recent net:</b> <span className={cashMovement.amount < 0 ? 'summary-alert' : cashMovement.amount > 0 ? 'summary-positive' : ''}>{formatLedgerMoney(cashMovement.amount, true)}</span> · {cashMovement.label}
            </div>
          </article>
        </div>
      </section>

      <section className="panel weekly-actions-panel">
        <div className="weekly-actions-heading">
          <div>
            <h2>Weekly Actions</h2>
            <p>Red must be resolved. Amber is optional. Green is already handled by staff.</p>
          </div>
          <span className={`status-pill ${hasCoachChanges ? 'override' : ''}`}>{hasCoachChanges ? `${coachModifiedAreas.length} Coach-modified area${coachModifiedAreas.length === 1 ? '' : 's'}` : 'Staff plan active'}</span>
        </div>
        <div className="action-grid">
          {actions.map(action => {
            const unavailable = action.status === 'unavailable'
            return (
              <button
                key={action.phase}
                type="button"
                onClick={() => !unavailable && handleActionCard(action)}
                disabled={unavailable}
                className={`action-card action-${action.status}`}
                aria-label={`${action.label}: ${action.statusLabel}`}
              >
                <div className="action-card-topline">
                  <span className="action-status-label">{action.statusLabel}</span>
                  {action.badge && <span className="action-badge">{action.badge}</span>}
                </div>
                <h3>{action.label}</h3>
                <p>{action.description}</p>
                {!unavailable && <span className="action-card-cta">{action.status === 'required' ? 'Resolve' : action.status === 'optional' ? 'Review or keep staff choice' : action.phase === 'confirmation' ? 'Final review before match' : 'View details'}</span>}
              </button>
            )
          })}
        </div>
      </section>

      {debut && (
        <section className="panel active-override">
          <strong>Active Coach Override:</strong> D2-B — Ari Dane replaces Heath Mercer. <button type="button" className="restore" onClick={() => onAction('player-detail', 'ari-dane')}>View / Restore</button>
        </section>
      )}

      <div className="dashboard-actions">
        {!isBye && (state.round === 3 || state.round === 4) && <button type="button" className="text-button" onClick={() => onAction('player-detail', 'ari-dane')}>Review Ari decision</button>}
        <div className={`match-commitment ${isReady ? 'ready' : 'blocked'}`}>
          <div className="match-commitment-copy">
            <span className="eyebrow">{isBye ? isFinals ? kingsportFinalsAlive ? 'Finals advancement' : 'Post-season fast track' : 'Weekly advancement' : isReady ? isFinals ? 'Current final' : 'Final action before match' : 'Match blocked'}</span>
            <strong>{isBye ? isFinals ? kingsportFinalsAlive ? 'Only this week’s scheduled AI finals will be simulated.' : 'Kingsport’s season is over. Simulate the remaining finals in one step and move to list management.' : 'No Kingsport match will be simulated.' : isReady ? isFinals ? 'Starting the final resolves this stage immediately.' : 'Starting the match resolves this round immediately.' : 'Complete the required decision before starting the match.'}</strong>
          </div>
          <button id={isBye ? 'bye-week-control' : 'match-start-control'} type="button" className={`primary-action ${isReady ? isBye ? 'bye-week-action' : 'match-start-action' : 'required-action'}`} onClick={handlePrimaryAction}>
            {isBye ? isFinals ? kingsportFinalsAlive ? `Advance Finals Week ${state.round - 19}` : 'Simulate Remaining Finals' : 'Complete Bye Week' : isReady ? hasCoachChanges ? `Start ${isFinals ? 'Final' : 'Match'} — Use Coach-Modified Plan` : `Start ${isFinals ? 'Final' : 'Match'} — Use Staff Plan` : `Resolve ${requiredActions.length} Required Action${requiredActions.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
